import {
  Button,
  CheckIcon,
  Modal,
  ModalProps,
  useCombobox,
  Combobox as Combo,
  ActionIcon,
  CloseButton,
  Loader
} from '@mantine/core'
import {useForm} from '@mantine/form'
import {ButtonNew} from 'components/Buttons'
import {useEffect, useState} from 'react'
import {HiArchive, HiSelector, HiStar} from 'react-icons/hi'
import ReactMarkdown from 'react-markdown'
import {pick, sortBy} from 'remeda'
import {usePortal} from './portal'
import {Workspace as W} from './workspace'
import {WorkspaceForm} from './admin/AdminWorkspace'
import Link from 'next/link'
import {useRouter} from 'next/router'
import {tourTipProps} from './tourTypes'
import {twMerge} from 'tailwind-merge'
import {showError} from 'components/notify'
import {HiMagnifyingGlass} from 'react-icons/hi2'
import {fmtDate} from 'shared/format'
import {groupMap} from 'shared/collection'
import {isNullOrEmpty} from 'shared/collection'
import {LabelEmptyList} from 'shared/ui/Labels'
import {env} from 'utils/env'
import {useNavigationLoading} from 'components/hooks/useNavigationLoading'

const workspaceUrl = (id: string) => `/?workspace=${id}`

export const WorkspaceSelect = ({className}: {className?: string; ref?: React.RefObject<HTMLDivElement | null>}) => {
  const {profiles, profile, workspace_id, updateProfile} = usePortal()
  const workspaces = profiles?.forSelect?.map(p => ({
    ...p.workspace,
    id: p.workspace_id,
    favorite: p.favorite,
    profileId: p.id,
    last_activity: p.last_activity
  }))
  const workspaceCombo = useCombobox({
    onDropdownClose: () => workspaceCombo.resetSelectedOption()
  })
  const [showArchived, setShowArchived] = useState(false)
  const [newWorkspaceOpen, setNewWorkspaceOpen] = useState(false)
  const workspaceSelectable =
    !env.NEXT_PUBLIC_SELF_HOST &&
    ((profiles?.forSelect?.length ?? 0) > 1 || profiles.global?.hasPermission('new-workspace'))
  const [search, setSearch] = useState('')
  const workspacesFiltered = sortBy(
    workspaces,
    [w => w.favorite ?? false, 'desc'],
    [w => w.last_activity ?? 'z', 'desc']
  )
    .filter(
      w =>
        w.name.toLocaleLowerCase().includes(search.trim().toLowerCase()) &&
        (w.id == workspace_id || showArchived || !w.archived)
    )
    .map(w => ({
      ...w,
      setFavorite: (v: boolean) => {
        updateProfile({favorite: v}, {id: w.profileId, noNotify: true})
      }
    }))
  const {isNavigating, startNavigation} = useNavigationLoading()

  return (
    <>
      {workspaceSelectable ?
        <Combo
          store={workspaceCombo}
          onOptionSubmit={o => {
            workspaceCombo.closeDropdown()
          }}
          middlewares={{shift: true}}
          preventPositionChangeWhenVisible={false}
          withinPortal
          position='bottom-start'
          transitionProps={{duration: 200, transition: 'pop'}}>
          <Combo.Target>
            {isNavigating ?
              <Loader />
            : <button
                {...tourTipProps('workspace-select')}
                onClick={() => workspaceCombo.toggleDropdown()}
                className={twMerge('text-left', className)}>
                <div className='flex items-center gap-2'>
                  <div className='sensitive overflow-hidden text-ellipsis'>{profile?.workspace?.name}</div>
                  <HiSelector className='shrink-0' />
                </div>
              </button>
            }
          </Combo.Target>
          <Combo.Dropdown style={{padding: '0.5em'}}>
            <Combo.Empty className='flex items-center !p-0'>
              <Combo.Search
                value={search}
                onChange={event => setSearch(event.currentTarget.value)}
                rightSection={<HiMagnifyingGlass />}
                placeholder='Search workspaces'
                autoFocus
                size='md'
                className='grow'
              />
              {search.length > 0 && <CloseButton className='' onClick={() => setSearch('')} />}
            </Combo.Empty>
            <Combo.Options className='max-h-[70vh] overflow-y-auto mx-[-0.5em] px-[0.5em] mb-6 min-w-[25em] max-w-[96vw]'>
              {isNullOrEmpty(workspacesFiltered) ?
                <LabelEmptyList className='mt-4'>No workspaces found</LabelEmptyList>
              : groupMap(
                  workspacesFiltered,
                  w => (w.favorite ? 'Favourites' : 'Workspaces'),
                  (fav, ws) => (
                    <Combo.Group key={fav} label={fav}>
                      {ws.map(w => (
                        <Combo.Option className='flex items-center gap-1' key={w.id} value={w.id}>
                          <Link
                            href={workspaceUrl(w.id)}
                            className={`max-w-[30em] my-1 block unstyled grow`}
                            onClick={() => startNavigation()}>
                            <div className='flex gap-2 items-center'>
                              {w.id == workspace_id && <CheckIcon size='1em' color='gray' />}
                              <b className='sensitive'>{w.name}</b>
                              {w.archived && <HiArchive />}
                            </div>
                            <ReactMarkdown className='text-base font-normal sensitive'>{w.description}</ReactMarkdown>
                            {w.last_activity && (
                              <div className='opacity-50 text-sm'>Used {fmtDate(w.last_activity)}</div>
                            )}
                          </Link>
                          {
                            <ActionIcon
                              onClick={e => {
                                e.stopPropagation()
                                w.setFavorite(!w.favorite)
                              }}>
                              <HiStar
                                color={w.favorite ? 'var(--mantine-color-yellow-7)' : ''}
                                style={{opacity: w.favorite ? 1 : 0.2}}
                              />
                            </ActionIcon>
                          }
                        </Combo.Option>
                      ))}
                    </Combo.Group>
                  )
                )
              }
            </Combo.Options>
            <Combo.Footer>
              {profiles.global?.hasPermission('new-workspace') == true && (
                <div className='flex justify-between'>
                  <ButtonNew
                    onClick={() => {
                      workspaceCombo.closeDropdown()
                      setNewWorkspaceOpen(true)
                    }}>
                    New Workspace
                  </ButtonNew>
                  <Button leftSection={<HiArchive />} onClick={() => setShowArchived(!showArchived)}>
                    {showArchived ? 'Hide archived' : 'show archived'}
                  </Button>
                </div>
              )}
            </Combo.Footer>
          </Combo.Dropdown>
        </Combo>
      : <></>}
      <NewWorkspaceModal opened={newWorkspaceOpen} onClose={() => setNewWorkspaceOpen(false)} />
    </>
  )
}

export const WorkspaceInlineSelect = ({className}: {className?: string}) => {
  const {profiles} = usePortal()
  const workspaces = profiles?.forSelect?.map(p => ({
    ...p.workspace,
    id: p.workspace_id,
    favorite: p.favorite,
    last_activity: p.last_activity
  }))
  const workspacesFiltered = sortBy(
    workspaces,
    [w => w.favorite ?? false, 'desc'],
    [w => w.last_activity ?? 'z', 'desc']
  )

  return (
    <div className={twMerge('flex flex-col gap-2', className)}>
      {isNullOrEmpty(workspacesFiltered) ?
        <LabelEmptyList className='col-span-full mt-4'>No workspaces found</LabelEmptyList>
      : groupMap(
          workspacesFiltered,
          w => (w.favorite ? 'Favourites' : 'Workspaces'),
          (fav, ws) => (
            <div key={fav} className='flex flex-col gap-2'>
              {ws.map(w => (
                <a
                  key={w.id}
                  href={workspaceUrl(w.id)}
                  className={twMerge(
                    '!text-[--mantine-color-text] first-letter:block border border-gray-200 rounded p-3 hover:border-primary/50 hover:bg-primary/10 cursor-pointer transition-colors'
                  )}>
                  <div className='flex gap-2 items-center justify-between'>
                    <div className='flex gap-2 items-center'>
                      <b className='sensitive'>{w.name}</b>
                    </div>
                    {w.favorite && <HiStar color='var(--mantine-color-yellow-7)' />}
                  </div>
                  <ReactMarkdown className='text-base font-normal sensitive'>{w.description}</ReactMarkdown>
                  {w.last_activity && <div className='opacity-50 text-sm'>Used {fmtDate(w.last_activity)}</div>}
                </a>
              ))}
            </div>
          )
        )
      }
    </div>
  )
}

export const NewWorkspaceModal = ({...props}: Pick<ModalProps, 'opened' | 'onClose'>) => {
  const {opened, onClose} = props
  const {profile} = usePortal()
  const form = useForm({
    initialValues: W.zCfg.parse({}),
    validate: W.cfgValidator,
    validateInputOnBlur: true
  })
  const {values, isValid, validate, reset} = form

  useEffect(() => {
    if (!opened) return
    reset()
  }, [opened])

  const {create} = W.useWorkspaces()
  const router = useRouter()

  return (
    profile && (
      <Modal size={'lg'} title={<h3>Create workspace</h3>} {...pick(props, ['opened', 'onClose'])}>
        <WorkspaceForm
          form={form}
          created_by={profile.id}
          hideOwner
          onSubmit={form.onSubmit(async v => {
            if (validate().hasErrors) return
            create.mutate(values, {
              onSuccess: r => {
                onClose()
                router.push(workspaceUrl(r.workspace_id))
              },
              onError: e => showError(e, 'Error creating workspace')
            })
          })}>
          <Button
            loading={create.isPending}
            className='self-end mb-4 mt-8'
            variant='filled'
            type='submit'
            disabled={!isValid()}>
            Create workspace
          </Button>
        </WorkspaceForm>
      </Modal>
    )
  )
}
